import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { news } from "@/mocks/news";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 5;

export default function NewsPage() {
  useDocumentMeta(
    "影视资讯 - 欧美三级影视最新动态与专题",
    "欧美三级影视资讯频道，提供欧美影视最新动态、影展资讯、影片推荐与深度专题，聚焦影视新闻与行业观察，了解最新影视趋势。",
    "欧美影视资讯,影视新闻,影展资讯,影片推荐,欧美三级资讯,影视专题"
  );

  const sorted = useMemo(() => [...news].sort((a, b) => (a.date < b.date ? 1 : -1)), []);
  const categories = useMemo(() => Array.from(new Set(news.map((n) => n.category))), []);
  const [activeCat, setActiveCat] = useState("全部");
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () => (activeCat === "全部" ? sorted : sorted.filter((n) => n.category === activeCat)),
    [activeCat, sorted]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const goPage = (p: number) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="mx-auto max-w-[1240px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "影视资讯" }]} />

      <header className="mt-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">影视资讯</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          聚焦影视新闻与深度专题，为你带来欧美影视最新动态、影展观察、影片推荐与行业趋势解读。
        </p>
      </header>

      <div className="mt-5 flex flex-wrap gap-2">
        {["全部", ...categories].map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => {
              setActiveCat(cat);
              setPage(1);
            }}
            className={[
              "cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
              activeCat === cat
                ? "bg-primary-600 text-background-50"
                : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700",
            ].join(" ")}
          >
            {cat}
          </button>
        ))}
      </div>

      {paged.length > 0 ? (
        <>
          <div className="mt-6 space-y-4">
            {paged.map((item) => (
              <article
                key={item.id}
                className="group overflow-hidden rounded-lg border border-background-200/80 bg-background-50 transition-all duration-300 hover:border-primary-300 hover:shadow-md"
              >
                <Link to={`/news-detail/${item.id}`} className="flex cursor-pointer flex-col sm:flex-row">
                  <div className="h-44 w-full shrink-0 overflow-hidden bg-background-200 sm:h-auto sm:w-56 md:w-64">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 影视资讯`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-4 md:p-5">
                    <div className="flex items-center gap-2">
                      <span className="rounded-md bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-700">
                        {item.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-secondary-500">
                        <i className="ri-calendar-line"></i>
                        {item.date}
                      </span>
                    </div>
                    <h2 className="mt-2.5 line-clamp-2 font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                      {item.title}
                    </h2>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-secondary-500">{item.summary}</p>
                    <span className="mt-auto flex items-center gap-1 pt-3 text-xs font-medium text-primary-700">
                      阅读全文
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>

          {totalPages > 1 && (
            <nav aria-label="分页" className="mt-8 flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => goPage(currentPage - 1)}
                className="flex h-9 cursor-pointer items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i className="ri-arrow-left-s-line"></i>
                上一页
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => goPage(p)}
                  className={[
                    "h-9 w-9 cursor-pointer rounded-md border text-sm font-medium transition-colors",
                    p === currentPage
                      ? "border-primary-600 bg-primary-600 text-background-50"
                      : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700",
                  ].join(" ")}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => goPage(currentPage + 1)}
                className="flex h-9 cursor-pointer items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                下一页
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </nav>
          )}
        </>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-secondary-500">
            <i className="ri-newspaper-line"></i>
          </span>
          <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">暂无相关资讯</h3>
          <p className="mt-2 text-sm text-secondary-500">该分类下暂时没有内容，试试其他分类。</p>
          <button
            type="button"
            onClick={() => {
              setActiveCat("全部");
              setPage(1);
            }}
            className="mt-5 flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-refresh-line"></i>
            查看全部资讯
          </button>
        </div>
      )}
    </div>
  );
}